import { useState, useEffect } from "react";
import BaseModal from "../../../BaseComponents/BaseModal/BaseModal";
import SelectBox from "../../../BaseComponents/SelectBox/SelectBox";
import styles from "./RegisterPaymentModal.module.css";
import { getAccounts } from "../../../../api/finance/accounts";

const emptyForm = {
  bookingRef: "",
  customerName: "",
  customerEmail: "",
  supplierName: "",
  supplierId: "",
  accountId: "",
  paymentMethod: "",
  amount: "",
  currency: "EUR",
  date: new Date().toISOString().split('T')[0],
  dueDate: "",
  reference: "",
  airlinePnr: "",
  supplierReferenceNo: "",
  transactionId: "",
  notes: "",
  status: "Pending"
};

const toForm = (data, paymentType) => {
  if (!data) return emptyForm;
  
  if (paymentType === "incoming") {
    return {
      bookingRef: data.bookingRef || "",
      customerName: data.customerName || "",
      customerEmail: data.customerEmail || "",
      accountId: data.accountId || "",
      paymentMethod: data.paymentMethod || "",
      amount: data.amount || "",
      currency: data.currency || "EUR",
      date: data.date || new Date().toISOString().split('T')[0],
      reference: data.reference || "",
      airlinePnr: data.airlinePnr || "",
      supplierReferenceNo: data.supplierReferenceNo || "",
      transactionId: data.transactionId || "",
      notes: data.notes || "",
      status: data.status || "Pending"
    };
  } else {
    // Outgoing payment
    return {
      bookingRef: data.bookingRef || "",
      supplierName: data.supplierName || "",
      supplierId: data.supplierId || "",
      accountId: data.accountId || "",
      paymentMethod: data.paymentMethod || "",
      amount: data.amount || "",
      currency: data.currency || "EUR",
      date: data.date || new Date().toISOString().split('T')[0],
      dueDate: data.dueDate || "",
      reference: data.reference || "",
      airlinePnr: data.airlinePnr || "",
      supplierReferenceNo: data.supplierReferenceNo || "",
      transactionId: data.transactionId || "",
      notes: data.notes || "",
      status: data.status || "Pending"
    };
  }
};

const RegisterPaymentModal = ({ 
  open, 
  onClose, 
  mode = "add", 
  initialData, 
  onSave, 
  saveLoading = false,
  paymentType = "incoming" // "incoming" or "outgoing"
}) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [accounts, setAccounts] = useState([]);

  // Fetch accounts filtered by usage whenever the modal opens or paymentType changes
  useEffect(() => {
    if (!open) return;
    const usageFilter = paymentType === "incoming" ? "client" : "supplier";
    getAccounts({ usage: usageFilter, active: true, limit: 100 })
      .then((res) => {
        const rows = res.data || [];
        setAccounts(rows.map((acc) => ({
          value: String(acc.id),
          label: acc.account_name,
          type: acc.account_type === 'bank_account' ? 'bank'
              : acc.account_type === 'card_processing' ? 'card'
              : 'cash',
        })));
      })
      .catch(() => setAccounts([]));
  }, [open, paymentType]);

  const selectedAccountType = accounts.find((a) => a.value === form.accountId)?.type;

  const paymentMethods = [
    "Bank Transfer", "SEPA", "SWIFT",
    "Visa", "Mastercard", "Amex", "Stripe", "PayPal",
    ...(selectedAccountType === "card" ? ["Payment Terminal"] : []),
    "Cash",
  ];

  const currencies = ["EUR", "USD", "GBP", "CHF"];

  const statuses = paymentType === "incoming" 
    ? ["Pending", "Completed", "Failed"]
    : ["Pending", "Completed", "Scheduled"];

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialData) {
      setForm(toForm(initialData, paymentType));
    } else if (mode === "add") {
      setForm({
        ...emptyForm,
        date: new Date().toISOString().split('T')[0]
      });
    }
    setErrors({});
  }, [open, mode, initialData, paymentType]);

  const handleChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // If account changes, clear paymentMethod so an invalid method isn't kept
      if (key === "accountId") next.paymentMethod = "";
      return next;
    });
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!form.bookingRef.trim()) {
      newErrors.bookingRef = "Booking reference is required";
    }

    if (paymentType === "incoming") {
      if (!form.customerName.trim()) {
        newErrors.customerName = "Customer name is required";
      }
    } else {
      if (!form.supplierName.trim()) {
        newErrors.supplierName = "Supplier name is required";
      }
    }

    if (!form.accountId) {
      newErrors.accountId = "Account is required";
    }

    if (!form.paymentMethod) {
      newErrors.paymentMethod = "Payment method is required";
    }

    if (!form.amount || parseFloat(form.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }

    if (!form.date) {
      newErrors.date = "Date is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      const payload = {
        ...form,
        amount: parseFloat(form.amount)
      };
      onSave(payload);
    }
  };

  const title = paymentType === "incoming"
    ? (mode === "add" ? "Register Client Payment" : "Edit Client Payment")
    : (mode === "add" ? "Register Supplier Payment" : "Edit Supplier Payment");

  const footer = (
    <>
      <button onClick={onClose} className={styles.cancelBtn} disabled={saveLoading}>
        Cancel
      </button>
      <button onClick={handleSubmit} className={styles.saveBtn} disabled={saveLoading}>
        {saveLoading ? "Saving..." : mode === "add" ? "Register Payment" : "Update Payment"}
      </button>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={title}
      footer={footer}
      width="700px"
    >
      <div className={styles.form}>
        <div className={styles.formGrid}>
          {/* Booking Reference */}
          <div className={styles.formGroup}>
            <label>
              Booking Reference <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              value={form.bookingRef}
              onChange={(e) => handleChange("bookingRef", e.target.value)}
              placeholder="e.g., BK-2024-001234"
              className={errors.bookingRef ? styles.inputError : ""}
            />
            {errors.bookingRef && <span className={styles.errorText}>{errors.bookingRef}</span>}
          </div>

          {/* Customer/Supplier Name */}
          {paymentType === "incoming" ? (
            <div className={styles.formGroup}>
              <label>
                Customer Name <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={form.customerName}
                onChange={(e) => handleChange("customerName", e.target.value)}
                placeholder="e.g., John Doe"
                className={errors.customerName ? styles.inputError : ""}
              />
              {errors.customerName && <span className={styles.errorText}>{errors.customerName}</span>}
            </div>
          ) : (
            <div className={styles.formGroup}>
              <label>
                Supplier Name <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={form.supplierName}
                onChange={(e) => handleChange("supplierName", e.target.value)}
                placeholder="e.g., Hotel Supplier"
                className={errors.supplierName ? styles.inputError : ""}
              />
              {errors.supplierName && <span className={styles.errorText}>{errors.supplierName}</span>}
            </div>
          )}
        </div>

        {/* Customer Email (incoming only) */}
        {paymentType === "incoming" && (
          <div className={styles.formGroup}>
            <label>Customer Email</label>
            <input
              type="email"
              value={form.customerEmail}
              onChange={(e) => handleChange("customerEmail", e.target.value)}
              placeholder="e.g., customer@email.com"
            />
          </div>
        )}

        <div className={styles.formGrid}>
          {/* Account */}
          <div className={styles.formGroup}>
            <label>
              Account <span className={styles.required}>*</span>
            </label>
            <select
              value={form.accountId}
              onChange={(e) => handleChange("accountId", e.target.value)}
              className={errors.accountId ? styles.inputError : ""}
            >
              <option value="">Select account</option>
              {accounts.map((acc) => (
                <option key={acc.value} value={acc.value}>
                  {acc.label}
                </option>
              ))}
            </select>
            {errors.accountId && <span className={styles.errorText}>{errors.accountId}</span>}
          </div>

          {/* Payment Method */}
          <div className={styles.formGroup}>
            <label>
              Payment Method <span className={styles.required}>*</span>
            </label>
            <select
              value={form.paymentMethod}
              onChange={(e) => handleChange("paymentMethod", e.target.value)}
              className={errors.paymentMethod ? styles.inputError : ""}
            >
              <option value="">Select method</option>
              {paymentMethods.map((method) => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
            {errors.paymentMethod && <span className={styles.errorText}>{errors.paymentMethod}</span>}
          </div>
        </div>

        <div className={styles.formGrid}>
          {/* Amount */}
          <div className={styles.formGroup}>
            <label>
              Amount <span className={styles.required}>*</span>
            </label>
            <input
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => handleChange("amount", e.target.value)}
              placeholder="0.00"
              className={errors.amount ? styles.inputError : ""}
            />
            {errors.amount && <span className={styles.errorText}>{errors.amount}</span>}
          </div>

          {/* Currency */}
          <div className={styles.formGroup}>
            <label>
              Currency <span className={styles.required}>*</span>
            </label>
            <select
              value={form.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
            >
              {currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.formGrid}>
          {/* Date */}
          <div className={styles.formGroup}>
            <label>
              Payment Date <span className={styles.required}>*</span>
            </label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => handleChange("date", e.target.value)}
              className={errors.date ? styles.inputError : ""}
            />
            {errors.date && <span className={styles.errorText}>{errors.date}</span>}
          </div>

          {/* Due Date (outgoing only) */}
          {paymentType === "outgoing" && (
            <div className={styles.formGroup}>
              <label>Due Date</label>
              <input
                type="date"
                value={form.dueDate}
                onChange={(e) => handleChange("dueDate", e.target.value)}
              />
            </div>
          )}

          {/* Status */}
          <div className={styles.formGroup}>
            <label>Status</label>
            <select
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Reference */}
        <div className={styles.formGroup}>
          <label>Payment Reference</label>
          <input
            type="text"
            value={form.reference}
            onChange={(e) => handleChange("reference", e.target.value)}
            placeholder="e.g., PAY-IN-20240310-001"
          />
        </div>

        {/* Transaction ID */}
        <div className={styles.formGroup}>
          <label>Transaction ID</label>
          <input
            type="text"
            value={form.transactionId}
            onChange={(e) => handleChange("transactionId", e.target.value)}
            placeholder="e.g., TXN-20240310-001"
          />
        </div>

        {/* Notes */}
        <div className={styles.formGroup}>
          <label>Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="Add any additional notes..."
            rows="3"
            className={styles.textarea}
          />
        </div>
      </div>
    </BaseModal>
  );
};

export default RegisterPaymentModal;